import React, { useState, useEffect, useContext } from "react";
import { DynamicContext } from "../../Context/DynamicContext";
import { post, deleteRequest, get } from "../../helper/Api_helper";
import {
  VYLABS_BASE_URL,
  GET_TENANT_DETAILS,
  UPLOAD_TENANT_LOGO,
  REMOVE_TENANT_LOGO,
} from "../../helper/url_helper";
import {
  TENANT_CORE_API_KEY,
  TENANT_PRIME_API_KEY,
} from "../../Tenant-Config/TenantConfig";
import { Toast } from "../../Common/Toast-Snackbar/ToastSnackbar";
import Button from "../../Common/Button/Button";
import copyIcon from "../../Assets/Images/copy-icon.png";
import OpenBtnIcon from "../../Assets/Images/open-portal-icon.svg";
import "./Settings.css";
import PlaceholderIcon from "../../Assets/Images/image-placeholder-loader.png";
import BannerUploadIcon from "../../Assets/Images/banner-upload-icon.png";
import uploadIcon from "../../Assets/Images/upload-button.svg";
import editIcon from "../../Assets/Images/lucide_edit-whiteicon.svg";
import deleteIcon from "../../Assets/Images/delete-button-icon.png";
import ContentDialog from "../../Common/Content-Dialog/ContentDialog";

function PublicCareerPortal() {
  const { isDarkMode, tenantDetails, setTenantDetails } =
    useContext(DynamicContext);
  const [publicJobUrl, setPublicJobUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [embedCode, setEmbedCode] = useState("");
  const [isTenantLogoModalOpen, setIsTenantLogoModalOpen] = useState(false);
  const [tenantLogoLoading, setTenantLogoLoading] = useState(false);
  const fetchTenantDetails = async () => {
    try {
      setIsLoading(true);
      const requestConfig = {
        headers: {
          "X-API-Key": TENANT_CORE_API_KEY,
        },
      };
      const response = await get(
        VYLABS_BASE_URL,
        GET_TENANT_DETAILS,
        requestConfig,
      );
      setTenantDetails(response?.tenant);
      localStorage.setItem("tenant", JSON.stringify(response));

      const publicBoardSlug = response?.tenant?.public_board_slug;
      if (publicBoardSlug) {
        const publicUrl = `${window.location.origin}/board/${publicBoardSlug}/jobs`;
        setPublicJobUrl(publicUrl);
        const embedHtml = `<iframe
  src="${publicUrl}"
  width="100%"
  height="500"
  frameborder="0"
  scrolling="no"
  loading="lazy"
  title="Career Portal">
</iframe>`;
        setEmbedCode(embedHtml);
      }
    } catch (err) {
      console.error("PublicCareerPortal GET_TENANT_DETAILS Error:", err);
      Toast("error", "Failed to fetch portal details");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTenantDetails();
  }, []);

  const handleCopyLink = () => {
    if (publicJobUrl) {
      navigator.clipboard.writeText(publicJobUrl);
      Toast("success", "Public career portal URL copied to clipboard!");
    }
  };

  const handleOpenPortal = () => {
    if (publicJobUrl) {
      window.open(publicJobUrl, "_blank");
    }
  };

  const handleCopyEmbedCode = () => {
    if (embedCode) {
      navigator.clipboard.writeText(embedCode);
      Toast("success", "Embed code copied to clipboard!");
    }
  };

  const tenantName =
    tenantDetails?.name ||
    tenantDetails?.company_name ||
    tenantDetails?.tenant_name ||
    "";
  const tenantLogoSrc = tenantDetails?.logo_key || PlaceholderIcon;

  const handleTenantLogoOpenModal = () => {
    setIsTenantLogoModalOpen(true);
  };

  const handleTenantLogoFileUpload = async (file) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      Toast(
        "warning",
        "Invalid file type. Only JPG, JPEG, or PNG are allowed.",
      );
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      Toast("warning", "Please upload an image less than 5MB.");
      return;
    }

    await handleUploadTenantLogo(file);
  };

  const handleTenantLogoSelectFile = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleTenantLogoFileUpload(file);
    } else {
      Toast("warning", "No file selected. Please try again.");
    }
    event.target.value = "";
  };

  const handleTenantLogoDrop = (event) => {
    event.preventDefault();
    if (tenantLogoLoading) {
      return;
    }
    const file = event.dataTransfer.files[0];
    if (file) {
      handleTenantLogoFileUpload(file);
    }
  };

  const handleTenantLogoDragOver = (event) => {
    event.preventDefault();
  };

  const handleUploadTenantLogo = async (fileToUpload) => {
    if (fileToUpload instanceof File) {
      setTenantLogoLoading(true);
      const formData = new FormData();
      formData.append("file", fileToUpload);
      const requestConfig = {
        headers: {
          "X-API-Key": TENANT_PRIME_API_KEY,
        },
      };
      try {
        const response = await post(
          VYLABS_BASE_URL,
          UPLOAD_TENANT_LOGO,
          formData,
          requestConfig,
        );

        if (response?.url) {
          setIsTenantLogoModalOpen(false);
          setTenantDetails((prev) => ({
            ...prev,
            logo_key: response.url,
          }));
          Toast("success", "Tenant logo uploaded successfully");
        } else {
          Toast("error", "Failed to upload tenant logo");
        }
      } catch (error) {
        console.error("Upload tenant logo error:", error);
        Toast("error", error?.message || "Failed to upload tenant logo");
      } finally {
        setTenantLogoLoading(false);
      }
    } else {
      Toast("warning", "Please select a valid image file to upload");
    }
  };

  const handleDeleteTenantLogo = async () => {
    try {
      const requestConfig = {
        headers: {
          "X-API-Key": TENANT_PRIME_API_KEY,
        },
      };
      await deleteRequest(
        VYLABS_BASE_URL,
        REMOVE_TENANT_LOGO,
        null,
        requestConfig,
      );
      setTenantDetails((prev) => ({
        ...prev,
        logo_key: null,
      }));
      Toast("success", "Tenant logo removed successfully");
    } catch (error) {
      console.error("Error deleting tenant logo:", error);
      Toast("error", "Failed to remove tenant logo");
    }
  };

  return (
    <div className="career-portal-container">
      <div className="settings-title-container">
        <h2>Public Career Portal</h2>
        <p>
          Allow candidates to browse open positions and apply without creating a
          VybogTal account.
        </p>
      </div>

      <div className="career-portal-content" style={{ marginTop: "30px" }}>
        <div
          style={{
            marginBottom: "30px",
            display: "flex",
            flexDirection: "column",
            border: "1px solid var(--border__color)",
            padding: "15px",
            borderRadius: "12px",
          }}
        >
          <div className="career-portal-share-header">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "20px", fontWeight: "700" }}>
                Organization Branding
              </h3>
              <strong className="user-name" style={{ color: "#6657f6" }}>
                ({tenantDetails?.name})
              </strong>
            </div>
            <p>
              The logo below will be used in the emails sent from this
              organization.
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <div
              style={{
                width: "100px",
                height: "100px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                padding: "8px",
                boxSizing: "border-box",
              }}
            >
              <img
                alt="Tenant Logo"
                src={tenantLogoSrc}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {tenantDetails?.logo_key && (
                <>
                  <Button
                    label="Edit Logo"
                    event={handleTenantLogoOpenModal}
                    backgroundColor="#6657f6"
                    color="#fff"
                    style={{
                      borderRadius: "10px",
                      height: "30px",
                    }}
                    iconSrc={editIcon}
                    iconWidth="15"
                  />
                  <Button
                    label="Remove Logo"
                    event={handleDeleteTenantLogo}
                    backgroundColor="#6657f6"
                    hoverd={false}
                    color="#fff"
                    style={{
                      border: isDarkMode
                        ? "1px solid rgba(255, 255, 255, 0.2)"
                        : "1px solid #dfe3eb",
                      borderRadius: "10px",
                      height: "30px",
                    }}
                    iconSrc={deleteIcon}
                    iconWidth="15"
                  />
                </>
              )}
              {!tenantDetails?.logo_key && (
                <Button
                  label="Upload Logo"
                  event={handleTenantLogoOpenModal}
                  backgroundColor="#6657f6"
                  color="#fff"
                  style={{
                    borderRadius: "10px",
                    height: "30px",
                    minWidth: "110px",
                  }}
                  iconSrc={uploadIcon}
                  iconWidth="15"
                />
              )}
            </div>
          </div>
        </div>
        <div className="career-portal-share-card">
          <div className="career-portal-share-header">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "20px", fontWeight: "700" }}>
                Public careers portal
              </h3>
              {tenantName && (
                <strong style={{ marginLeft: "8px", color: "#6657f6" }}>
                  ({tenantName})
                </strong>
              )}
            </div>
            <p>Distribute your portal URL to candidates.</p>
          </div>

          <div className="career-portal-actions-row">
            <div className="career-portal-url-box">
              {isLoading ? (
                "Loading..."
              ) : publicJobUrl ? (
                <span>{publicJobUrl}</span>
              ) : (
                "Slug not configured"
              )}
            </div>
            <Button
              label="Copy Link"
              event={handleCopyLink}
              disabled={!publicJobUrl || isLoading}
              iconSrc={copyIcon}
              iconWidth="16px"
              backgroundColor="transparent"
              hoverd={false}
              color={isDarkMode ? "#fff" : "#2c3342"}
              style={{
                border: isDarkMode
                  ? "1px solid rgba(255, 255, 255, 0.2)"
                  : "1px solid #dfe3eb",
                borderRadius: "10px",
                height: "38px",
              }}
            />
            <Button
              label="Open Portal"
              event={handleOpenPortal}
              disabled={!publicJobUrl || isLoading}
              iconSrc={OpenBtnIcon}
              iconWidth="16px"
              backgroundColor="#6657f6"
              color="#fff"
              style={{ borderRadius: "10px", height: "38px" }}
            />
          </div>

          <div className="career-portal-usage-box">
            <h4>Ways to use this link</h4>
            <p>- Add to your company website</p>
            <p>- Share on LinkedIn</p>
            <p>- Include in email campaigns</p>
            <p>- Send to recruitment agencies</p>
          </div>
        </div>
        <div className="career-portal-embed-card" style={{ marginTop: "30px" }}>
          <div className="career-portal-embed-header">
            <h3>Embed on your website</h3>
            <p>
              Place the code below wherever you want the embeddable to appear on
              your site (HTML editor, theme, template, etc.)
            </p>
          </div>

          <div className="career-portal-embed-code-container">
            <div className="career-portal-embed-code-box">
              <button
                style={{
                  backgroundColor: isDarkMode ? "#333" : "#fff",
                  border: isDarkMode
                    ? "1px solid rgba(255, 255, 255, 0.2)"
                    : "1px solid #dfe3eb",
                  borderRadius: "10px",
                  height: "38px",
                  padding: "0 20px",
                  color: isDarkMode ? "#fff" : "#2c3342",
                  cursor: "pointer",
                }}
                className="career-portal-code-copy-icon"
                type="button"
                onClick={handleCopyEmbedCode}
                disabled={!embedCode || isLoading}
                aria-label="Copy iframe code"
              >
                <img src={copyIcon} alt="" />
              </button>
              {isLoading ? (
                "Loading embed code..."
              ) : embedCode ? (
                <code>{embedCode}</code>
              ) : (
                "Embed code not available"
              )}
            </div>
          </div>
        </div>
      </div>
      <ContentDialog
        isOpen={isTenantLogoModalOpen}
        onClose={() => setIsTenantLogoModalOpen(false)}
        onCloseIconClose={() => setIsTenantLogoModalOpen(false)}
        showActionButton={false}
        contentWidth="40vw"
        overflowY={true}
        contentHeight="60vh"
        title="Upload Tenant Logo"
      >
        <div>
          <div
            className="profile-image-container"
            onDrop={handleTenantLogoDrop}
            onDragOver={handleTenantLogoDragOver}
          >
            <img
              src={BannerUploadIcon}
              alt="BannerUploadIcon"
              className="banner-upload-icon"
            />
            <span className="upload-text">Drag and drop images to upload</span>
            <div className="upload-button-container">
              <label htmlFor="file-input-tenant-logo">
                <Button
                  label={tenantLogoLoading ? "Uploading..." : "Select files"}
                  backgroundColor="#fff"
                  color="#000"
                  border="1px solid #ccc"
                  disabled={tenantLogoLoading}
                  event={() =>
                    document.getElementById("file-input-tenant-logo").click()
                  }
                />
              </label>
              <input
                id="file-input-tenant-logo"
                type="file"
                accept="image/jpeg, image/png"
                style={{ display: "none" }}
                onChange={(e) => handleTenantLogoSelectFile(e)}
                disabled={tenantLogoLoading}
              />
            </div>
          </div>
          <div className="info-container">
            <span className="info-span">
              JPG, JPEG, PNG Files are Allowed, Max Size 5MB.
            </span>
          </div>
        </div>
      </ContentDialog>
    </div>
  );
}

export default PublicCareerPortal;
